import { initializeApp } from "firebase/app";
import {
  browserSessionPersistence,
  getAuth,
  indexedDBLocalPersistence,
  RecaptchaVerifier,
  setPersistence,
  signInWithCustomToken,
  signInWithPhoneNumber
} from "firebase/auth";
import { addDoc, collection, doc, getDoc, getFirestore, setDoc, updateDoc } from "firebase/firestore";
import {
  getFunctions,
  httpsCallable
} from "firebase/functions";
import { getStorage } from "firebase/storage";
import { useMemo, useReducer } from "react";
import {
  useAuthState,
  useSendPasswordResetEmail,
  useSignInWithEmailAndPassword
} from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import { getOrDefault } from "../../Helpers/Utils";
import { setStoredToken, getStoredToken, clearStoredToken } from "../../Helpers/LocalPersistenceUtils";
import UserExistence from "../UserExistenceEnum";
import getDomain from "../../Helpers/DomainParser";

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_APPLICATION":
      return {
        ...state,
        applicationMode: action.payload.applicationMode,
        app: action.payload.app,
      };
    case "SET_AUTH":
      return {
        ...state,
        auth: action.payload.auth,
        tenantId: action.payload.tenantId,
      };
    case "SET_FIRESTORE":
      return {
        ...state,
        firestore: action.payload.firestore,
      };
    case "SET_FIREBASE_FUNCTIONS":
      return {
        ...state,
        serverlessFunctions: action.payload.serverlessFunctions,
      };

    case "SET_FIREBASE_STORAGE":
      return {
        ...state,
        storage: action.payload.storage,
      };
    case "SET_USER_PROFILE":
      return {
        ...state,
        userProfile: action.payload.userDocument,
      };
    default:
      return state;
  }
};

const useFirebase = (config) => {
  const [properties, dispatch] = useReducer(reducer, {});

  useFirebaseApplication(properties, dispatch);
  useFirebaseAuthState(properties, dispatch);
  useFirebaseFirestoreProperties(properties, dispatch);
  useFirebaseFunctionsProperties(properties, dispatch);
  useFirebaseStorage(properties, dispatch);
  useUserProfile(properties, dispatch, config);

  useFirebaseSignInWithEmailAndPassword(properties, config);
  useFirebaseSignInWithCustomToken(properties, config);
  useUnauthenticatedFirebaseSendPasswordResetLink(properties);
  useAuthenticatedFirebaseSendPasswordResetLink(properties);
  useDeleteUser(properties);
  useSubmitForm(properties, dispatch);
  useSignOut(properties, config);
  useSignOutAndReload(properties, config);
  useSessionCookieAutoSignIn(properties, config);
  useCompleted(properties);
  return properties;
};

const useFirebaseApplication = (properties, dispatch) => {
  if (!properties.app) {
    const config = {};
    config.appId = process.env.REACT_APP_FIREBASE_APP_ID;
    config.apiKey = process.env.REACT_APP_FIREBASE_API_KEY;
    config.authDomain = process.env.REACT_APP_FIREBASE_AUTH_DOMAIN;
    config.projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
    config.storageBucket = process.env.REACT_APP_FIREBASE_STORAGE_BUCKET;
    config.messagingSenderId =
      process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID;
    config.measurmentId = process.env.REACT_APP_FIREBASE_MEASUREMENT_ID;

    const applicationMode = process.env.REACT_APP_MODE;
    const app = initializeApp(config);

    dispatch({
      type: "SET_APPLICATION",
      payload: {
        applicationMode,
        app,
      },
    });
  }
};

const useFirebaseAuthState = (properties, dispatch) => {
  if (!properties.auth) {
    const auth = getAuth(properties.app);
    const tenantId = process.env.REACT_APP_FIREBASE_AUTH_TENANT_ID;
    dispatch({ type: "SET_AUTH", payload: { auth, tenantId } });
  }
  const [user, loading, error] = useAuthState(
    properties.auth ? properties.auth : getAuth(properties.app)
  );
  properties.user = user;
  properties.loading = loading;
  properties.error = error;
};

const useFirebaseFirestoreProperties = (properties, dispatch) => {
  if (!properties.firestore) {
    const firestore = {};
    const instance = getFirestore(properties.app);
    firestore.instance = instance;
    firestore.user = {
      collectionName: "users",
      fields: {
        uid: "uid",
        firstName: "first_name",
        lastName: "last_name",
        roles: "roles",
        photoURL: "photo_url"
      },
    };
    dispatch({ type: "SET_FIRESTORE", payload: { firestore } });
  }
};

const useFirebaseFunctionsProperties = (properties, dispatch) => {
  if (!properties.serverlessFunctions) {
    const instance = getFunctions(properties.app);
    const serverlessFunctions = {
      instance,
      methods: {
        deleteUser: "deleteUser",
        getUserInfo: "getUserInfo",
        createSessionCookie: "createSessionCookie",
        verifySessionCookie: "verifySessionCookie",
        revokeSessionCookie: "revokeSessionCookie",
        getEntitiesRelationsCount: "getEntitiesRelationsCount",
      },
    };
    dispatch({
      type: "SET_FIREBASE_FUNCTIONS",
      payload: { serverlessFunctions },
    });
  }
};
const useFirebaseStorage = (properties, dispatch) => {
  if (!properties.storage) {
    const instance = getStorage(properties.app);
    const storage = {
      instance,
    };
    dispatch({
      type: "SET_FIREBASE_STORAGE",
      payload: { storage },
    });
  }
};

const useUserProfile = (properties, dispatch, config) => {
  const { loading, user, firestore, serverlessFunctions } = properties;
  useMemo(() => {
    const userDocument = getUserDocumentWithInitialValue(firestore, serverlessFunctions, user?.uid, config, UserExistence.LOADING);
    dispatch({ type: "SET_USER_PROFILE", payload: { userDocument } });

    if (!loading && firestore && user) {
      const docRef = doc(
        firestore.instance,
        firestore.user.collectionName,
        user.uid
      );
      getDoc(docRef).then((value) => {
        if (value.exists()) {
          const userDocument = getUserDocumentWithActualData(
            firestore,
            serverlessFunctions,
            user,
            value.data(),
            config
          );
          dispatch({ type: "SET_USER_PROFILE", payload: { userDocument } });
        } else {
          const userDocument = getUserDocumentWithInitialValue(firestore, serverlessFunctions, user?.uid, config, UserExistence.NOT_EXISTS);
          dispatch({
            type: "SET_USER_PROFILE",
            payload: { userDocument },
          });
        }
      });
    }
  }, [loading, user, firestore, dispatch, config, serverlessFunctions]);
};

const getUserDocumentWithActualData = (firestore, serverlessFunctions, user, data, config) => {
  const uid = data[firestore.user.fields.uid];
  const firstName = data[firestore.user.fields.firstName];
  const lastName = data[firestore.user.fields.lastName];
  const displayName = [firstName, lastName].join(" ");
  const roles = data[firestore.user.fields.roles] || [];
  const photoURL = data[firestore.user.fields.photoURL] || user.photoURL || null;
  const profile = {
    userExistence: UserExistence.EXISTS,
    attributes: {
      ...data,
      uid,
      firstName,
      lastName,
      displayName,
      photoURL,
      roles,
    },
    methods: getUserProfileMethods(firestore, serverlessFunctions, uid, config)
  };
  return profile;
};

const getUserDocumentWithInitialValue = (firestore, serverlessFunctions, uid, config, userExistence) => {
  const initialValue = {
    userExistence: userExistence,
    attributes: {
      uid: null,
      firstName: null,
      lastName: null,
      displayName: null,
      photoURL: null,
      roles: [],
    },
    methods: getUserProfileMethods(firestore, serverlessFunctions, uid, config)
  };
  return initialValue;
};

const getUserProfileMethods = (firestore, serverlessFunctions, uid, config) => {
  return {
    updateUserProfile: (updateData) =>
      updateUserProfile(firestore, updateData, config),
    getUserInfo: (identifier) =>
      getUserInfo(serverlessFunctions, identifier),
    setupRecaptcha: (containerId) =>
      setupRecaptcha(containerId),
    sendPhoneOtp: (phoneNumber, recaptchaVerifier) =>
      sendPhoneOtp(phoneNumber, recaptchaVerifier),
    verifyPhoneOtp: (confirmationResult, otpCode) =>
      verifyPhoneOtp(confirmationResult, otpCode, serverlessFunctions, config),
    cleanupRecaptcha: () =>
      cleanupRecaptcha(),
    verifySessionCookie: () =>
      verifyStoredSessionCookie(serverlessFunctions, config),
    getEntitiesRelationsCount: (items) =>
      getEntitiesRelationsCount(serverlessFunctions, items),
    clearSessionCookie: () =>
      clearSessionCookie(config),
    createSessionCookie: (user) =>
      createAndStoreSessionCookie(serverlessFunctions, user, config),
    handleSessionExpiration: () => {
      // Clear session cookie and sign out user
      clearSessionCookie(config);
      // Get current auth instance and sign out if user is authenticated
      const auth = getAuth();
      if (auth?.currentUser) {
        auth.signOut();
      }
    },
  };
};

const updateUserProfile = async (firestore, updateData, config) => {
  const application = getOrDefault(config.application, {})
  const auth = getOrDefault(application.auth, {})
  const rolesAssigned = getOrDefault(auth.rolesAssigned, [])
  const finalRolesAssigned = new Set([...rolesAssigned, "normal", "user"]);
  try {
    // Get current authenticated user
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error("User must be authenticated to update profile");
    }

    const uid = currentUser.uid;

    // Create document reference
    const userDocRef = doc(firestore.instance, firestore.user.collectionName, uid);

    // Check if document exists
    const existingDoc = await getDoc(userDocRef);

    // Transform the data to match Firestore field names
    const firestoreData = {};

    // Handle additional data
    for (const [key, value] of Object.entries(updateData.additionalData || {})) {
      firestoreData[key] = value;
    }

    // Map standard fields
    if (updateData.firstName !== undefined) {
      firestoreData[firestore.user.fields.firstName] = updateData.firstName;
    }
    if (updateData.lastName !== undefined) {
      firestoreData[firestore.user.fields.lastName] = updateData.lastName;
    }

    // Add timestamps
    if (!existingDoc.exists()) {
      // New user - add creation timestamp and UID
      firestoreData.created_at = new Date();
      firestoreData[firestore.user.fields.uid] = uid;
      firestoreData[firestore.user.fields.roles] = Array.from(finalRolesAssigned);
    }
    firestoreData.updated_at = new Date();

    // Create or update the document
    if (existingDoc.exists()) {
      await updateDoc(userDocRef, firestoreData);
    } else {
      // Use setDoc for new documents to avoid merge issues
      await setDoc(userDocRef, firestoreData);
    }

    return { success: true };
  } catch (error) {
    console.error("Error updating profile:", error);
    throw new Error("Failed to update profile: " + error.message);
  }
};

const getUserInfo = async (serverlessFunctions, identifier) => {
  try {
    // Validate identifier
    if (!identifier || !identifier.trim()) {
      throw new Error("Identifier is required");
    }

    // Create callable function
    const getUserInfoFunction = httpsCallable(
      serverlessFunctions.instance,
      serverlessFunctions.methods.getUserInfo
    );

    // Call the cloud function
    const result = await getUserInfoFunction({
      identifier: identifier.trim()
    });

    // Return the response data
    return result.data;
  } catch (error) {
    console.error("Error getting user info:", error);

    // Handle Firebase Functions errors
    if (error.code) {
      switch (error.code) {
        case 'functions/invalid-argument':
          throw new Error("Invalid identifier format. Please provide a valid email or phone number.");
        case 'functions/internal':
          throw new Error("Service temporarily unavailable. Please try again later.");
        case 'functions/unauthenticated':
          throw new Error("Authentication required.");
        default:
          throw new Error(error.message || "An error occurred while getting user information.");
      }
    }

    throw new Error("Failed to get user information");
  }
};

// Session Cookie Management for SSO
const createAndStoreSessionCookie = async (serverlessFunctions, user, config) => {
  console.group('🔐 SSO Session Cookie Creation');
  try {
    // Validate inputs first
    console.log('📋 Validating inputs...');
    console.log('  - serverlessFunctions:', !!serverlessFunctions);
    console.log('  - user:', !!user);
    console.log('  - config:', !!config);

    if (!serverlessFunctions || !user || !config) {
      console.error('❌ Missing required parameters');
      console.groupEnd();
      return { success: false, reason: 'Missing required parameters' };
    }

    const ssoConfig = getOrDefault(config.application?.auth?.sso, {});
    console.log('📋 SSO Configuration:', {
      enabled: ssoConfig.enabled,
      cookieKey: ssoConfig.cookieKey,
      domain: ssoConfig.domain,
      hasConfig: !!ssoConfig
    });

    if (!ssoConfig.enabled || !ssoConfig.cookieKey) {
      console.warn('⚠️ SSO not enabled or not configured properly');
      console.groupEnd();
      return { success: false, reason: 'SSO not configured' };
    }

    if (!ssoConfig.domain) {
      ssoConfig.domain = getDomain(window.location.hostname);

    }
    if (!ssoConfig.domain) {
      console.warn('⚠️ SSO domain not configured and could not be determined');
      console.groupEnd();
      return { success: false, reason: 'SSO domain not configured' };
    }

    console.log('✅ SSO is properly configured');

    // Validate serverless functions configuration
    console.log('📋 Checking serverless functions...');
    console.log('  - instance:', !!serverlessFunctions.instance);
    console.log('  - methods:', serverlessFunctions.methods);

    if (!serverlessFunctions.instance || !serverlessFunctions.methods?.createSessionCookie) {
      console.error('❌ Session cookie cloud function not available');
      console.groupEnd();
      return { success: false, reason: 'Session cookie cloud function not available' };
    }

    console.log('✅ Serverless functions configured');

    // Get ID token from current user with timeout
    console.log('🔑 Getting ID token from user...');
    const idToken = await Promise.race([
      user.getIdToken(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('ID token retrieval timeout')), 10000)
      )
    ]);
    console.log('✅ ID token retrieved successfully');

    // Create session cookie via cloud function with timeout
    console.log('☁️ Calling cloud function to create session cookie...');
    const createSessionCookieFunction = httpsCallable(
      serverlessFunctions.instance,
      serverlessFunctions.methods.createSessionCookie
    );

    const result = await Promise.race([
      createSessionCookieFunction({
        idToken: idToken,
        expiresIn: 60 * 60 * 24 * 14 * 1000 // 14 days in milliseconds
      }),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Cloud function call timeout')), 15000)
      )
    ]);
    console.log('✅ Cloud function returned successfully:', {
      hasSessionCookie: !!result.data?.sessionCookie,
      expiresIn: result.data?.expiresIn
    });

    if (result.data?.sessionCookie) {
      try {
        // Store session cookie using persistence utils with timeout
        // Ensure domain starts with a dot for subdomain support
        const cookieDomain = ssoConfig.domain.startsWith('.') ? ssoConfig.domain : `.${ssoConfig.domain}`;

        console.log('💾 Storing session cookie...', {
          cookieKey: ssoConfig.cookieKey,
          domain: cookieDomain,
          cookieLength: result.data.sessionCookie.length
        });

        await Promise.race([
          setStoredToken(
            'cookie',
            ssoConfig.cookieKey,
            result.data.sessionCookie,
            cookieDomain,
            process.env.REACT_APP_CLOUD_PROVIDER // Use Firebase API key as encryption key for extra security
          ),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Cookie storage timeout')), 5000)
          )
        ]);

        console.log('✅ Session cookie created and stored successfully');
        console.groupEnd();
        return { success: true, sessionCookie: result.data.sessionCookie };
      } catch (storageError) {
        console.error('❌ Failed to store session cookie:', storageError);
        console.groupEnd();
        return { success: false, reason: 'Storage failed: ' + storageError.message };
      }
    } else {
      console.error('❌ No session cookie returned from cloud function');
      console.groupEnd();
      return { success: false, reason: 'No session cookie returned' };
    }
  } catch (error) {
    console.error('❌ Error creating session cookie:', error);
    console.error('Error details:', {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    console.groupEnd();
    return { success: false, reason: error.message };
  }
};

const revokeStoredSessionCookie = async (serverlessFunctions, config) => {
  try {
    // Validate inputs
    if (!serverlessFunctions || !config) {
      console.warn('⚠️ Missing required parameters for session revocation');
      return { success: false, reason: 'Missing required parameters' };
    }

    const ssoConfig = getOrDefault(config.application?.auth?.sso, {});

    if (!ssoConfig.enabled || !ssoConfig.cookieKey) {
      console.warn('⚠️ SSO not configured, skipping session revocation');
      return { success: false, reason: 'SSO not configured' };
    }

    // Validate serverless functions configuration
    if (!serverlessFunctions.instance || !serverlessFunctions.methods?.revokeSessionCookie) {
      console.warn('⚠️ Session cookie revocation function not available');
      return { success: false, reason: 'Revocation function not available' };
    }

    // Get stored session cookie
    const sessionCookie = await Promise.race([
      getStoredToken('cookie', ssoConfig.cookieKey, process.env.REACT_APP_CLOUD_PROVIDER),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Cookie retrieval timeout')), 5000)
      )
    ]).catch(() => null);

    if (!sessionCookie) {
      console.warn('⚠️ No session cookie found to revoke');
      return { success: false, reason: 'No session cookie found' };
    }

    console.log('🔒 Revoking session cookie on server...');

    // Revoke session cookie via cloud function with timeout
    const revokeSessionCookieFunction = httpsCallable(
      serverlessFunctions.instance,
      serverlessFunctions.methods.revokeSessionCookie
    );

    const result = await Promise.race([
      revokeSessionCookieFunction({
        sessionCookie: sessionCookie
      }),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Revocation timeout')), 10000)
      )
    ]);

    if (result.data?.success) {
      console.log('✅ Session cookie revoked on server successfully');
      return { success: true };
    } else {
      console.warn('⚠️ Session revocation returned unsuccessful response');
      return { success: false, reason: 'Revocation unsuccessful' };
    }
  } catch (error) {
    console.warn('⚠️ Error revoking session cookie (non-critical):', error.message);
    // Don't block sign-out if revocation fails
    return { success: false, reason: error.message };
  }
};

const clearSessionCookie = (config) => {
  try {
    // Validate config exists
    if (!config || typeof config !== 'object') {
      console.log('Invalid config provided, skipping session cookie clearing');
      return;
    }

    const ssoConfig = getOrDefault(config.application?.auth?.sso, {});

    if (!ssoConfig.enabled || !ssoConfig.cookieKey) {
      console.log('SSO not enabled or cookieKey not configured, skipping session cookie clearing');
      return;
    }

    // Ensure domain starts with a dot for subdomain support
    const cookieDomain = ssoConfig.domain.startsWith('.') ? ssoConfig.domain : `.${ssoConfig.domain}`;

    console.log('🗑️ Clearing session cookie:', {
      cookieKey: ssoConfig.cookieKey,
      domain: cookieDomain
    });

    // Clear session cookie using persistence utils with timeout protection
    try {
      clearStoredToken('cookie', ssoConfig.cookieKey);
      console.log('✅ Session cookie cleared from storage');
    } catch (clearError) {
      console.warn('⚠️ Failed to clear session cookie from storage:', clearError);
    }

    // Always clear via manual method to ensure it's removed across all subdomains
    try {
      // Clear with domain (for subdomain sharing)
      document.cookie = `${ssoConfig.cookieKey}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${cookieDomain}; SameSite=Lax; Secure`;
      // Also clear without domain (for current domain)
      document.cookie = `${ssoConfig.cookieKey}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax; Secure`;
      console.log('✅ Session cookie cleared via manual method');
    } catch (manualError) {
      console.warn('⚠️ Manual cookie clearing failed:', manualError);
    }
  } catch (error) {
    console.warn('⚠️ Error during session cookie clearing (non-critical):', error);
  }
};

const verifyStoredSessionCookie = async (serverlessFunctions, config) => {
  try {
    // Validate inputs
    if (!serverlessFunctions || !config) {
      return { success: false, reason: 'Missing required parameters' };
    }

    const ssoConfig = getOrDefault(config.application?.auth?.sso, {});

    if (!ssoConfig.enabled || !ssoConfig.cookieKey) {
      return { success: false, reason: 'SSO not configured' };
    }

    // Validate serverless functions configuration
    if (!serverlessFunctions.instance || !serverlessFunctions.methods?.verifySessionCookie) {
      return { success: false, reason: 'Session cookie verification function not available' };
    }

    // Get stored session cookie with timeout (encrypted with Firebase API key)
    const sessionCookie = await Promise.race([
      getStoredToken('cookie', ssoConfig.cookieKey, process.env.REACT_APP_CLOUD_PROVIDER),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Cookie retrieval timeout')), 5000)
      )
    ]);

    if (!sessionCookie) {
      return { success: false, reason: 'No session cookie found' };
    }

    // Verify session cookie via cloud function with timeout
    const verifySessionCookieFunction = httpsCallable(
      serverlessFunctions.instance,
      serverlessFunctions.methods.verifySessionCookie
    );

    const result = await Promise.race([
      verifySessionCookieFunction({
        sessionCookie: sessionCookie,
        checkRevoked: true
      }),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Verification timeout')), 10000)
      )
    ]);

    if (result.data?.customToken) {
      return {
        success: true,
        customToken: result.data.customToken,
        uid: result.data.uid,
        email: result.data.email
      };
    } else {
      // Session cookie invalid, clear it (non-blocking)
      try {
        clearSessionCookie(config);
      } catch (clearError) {
        console.warn('Failed to clear invalid session cookie:', clearError);
      }
      return { success: false, reason: 'Invalid session cookie response' };
    }
  } catch (error) {
    console.warn('Error verifying session cookie (non-critical):', error);

    // Only clear cookie if it's truly invalid (expired, revoked, malformed)
    // Don't clear on server errors (500, network issues) - the cookie might still be valid
    const shouldClearCookie = error.code && (
      error.code.includes('unauthenticated') ||
      error.code.includes('expired') ||
      error.code.includes('revoked') ||
      error.code.includes('invalid-argument')
    );

    if (shouldClearCookie) {
      console.warn('Session cookie is invalid, clearing it');
      try {
        clearSessionCookie(config);
      } catch (clearError) {
        console.warn('Failed to clear invalid session cookie:', clearError);
      }
    } else {
      console.warn('Server error during verification, keeping cookie for retry');
    }

    return { success: false, reason: error.message };
  }
};

const getEntitiesRelationsCount = async (serverlessFunctions, items) => {
  try {
    // Validate inputs
    if (!items || !Array.isArray(items)) {
      throw new Error("Items must be an array");
    }
    
    if (items.length === 0 || items.length > 10) {
      throw new Error("Items array must contain between 1 and 10 elements");
    }
    
    // Validate each item has required fields
    for (const item of items) {
      if (!item.parentEntityId || !item.childEntityCollection || !item.childFieldName) {
        throw new Error("Each item must have parentEntityId, childEntityCollection, and childFieldName");
      }
    }
    
    // Create callable function
    const getEntitiesRelationsCountFunction = httpsCallable(
      serverlessFunctions.instance,
      serverlessFunctions.methods.getEntitiesRelationsCount
    );
    
    // Call the cloud function with batch request
    const result = await getEntitiesRelationsCountFunction({
      items
    });
    
    // Return the results array
    return result.data;
  } catch (error) {
    console.error("Error getting entities relations count:", error);
    
    // Handle Firebase Functions errors
    if (error.code) {
      switch (error.code) {
        case 'functions/invalid-argument':
          throw new Error("Invalid request format. " + (error.message || ""));
        case 'functions/internal':
          throw new Error("Service temporarily unavailable. Please try again later.");
        case 'functions/unauthenticated':
          throw new Error("Authentication required.");
        default:
          throw new Error(error.message || "An error occurred while getting relations count.");
      }
    }
    
    throw new Error("Failed to get entities relations count");
  }
};

// Firebase Phone Authentication Methods
const setupRecaptcha = (containerId) => {
  try {
    const auth = getAuth();

    console.log('Setting up reCAPTCHA with container ID:', containerId);

    // Check if container exists
    const container = document.getElementById(containerId);
    if (!container) {
      console.error('reCAPTCHA container not found:', containerId);
      throw new Error(`reCAPTCHA container with ID '${containerId}' not found`);
    }

    console.log('reCAPTCHA container found:', container);

    // Clean up any existing reCAPTCHA thoroughly
    cleanupRecaptcha();

    // Validate auth instance
    if (!auth) {
      throw new Error('Firebase Auth instance not available');
    }

    console.log('Creating reCAPTCHA verifier...');

    // Create new reCAPTCHA verifier with correct v9 syntax
    window.recaptchaVerifier = new RecaptchaVerifier(containerId, {
      size: 'invisible',
      callback: () => {
        console.log('reCAPTCHA solved');
      },
      'expired-callback': () => {
        console.log('reCAPTCHA expired');
      }
    }, auth);

    console.log('reCAPTCHA setup completed for container:', containerId);
    return window.recaptchaVerifier;

  } catch (error) {
    console.error("Detailed error setting up reCAPTCHA:", error);
    console.error("Error code:", error.code);
    console.error("Error message:", error.message);
    console.error("Error stack:", error.stack);

    // Try to cleanup on error
    cleanupRecaptcha();
    throw new Error("Failed to setup reCAPTCHA verification: " + error.message);
  }
};

const cleanupRecaptcha = () => {
  try {
    if (window.recaptchaVerifier) {
      // Clear the verifier properly
      if (typeof window.recaptchaVerifier.clear === 'function') {
        window.recaptchaVerifier.clear();
      }

      // Remove the reCAPTCHA container elements if they exist
      const container = document.getElementById('recaptcha-container');
      if (container) {
        container.innerHTML = '';
      }

      // Clear the global reference
      window.recaptchaVerifier = null;
      console.log('reCAPTCHA cleaned up successfully');
    }

    // Also clear any leftover reCAPTCHA elements in the DOM
    const recaptchaElements = document.querySelectorAll('[id^="g-recaptcha"], .grecaptcha-badge');
    recaptchaElements.forEach(element => {
      try {
        element.remove();
      } catch (e) {
        console.warn('Could not remove reCAPTCHA element:', e);
      }
    });

  } catch (error) {
    console.error("Error cleaning up reCAPTCHA:", error);

    // Force cleanup by clearing the container and global reference
    try {
      const container = document.getElementById('recaptcha-container');
      if (container) {
        container.innerHTML = '';
      }
      window.recaptchaVerifier = null;
    } catch (fallbackError) {
      console.error("Fallback cleanup also failed:", fallbackError);
    }
  }
};

const sendPhoneOtp = async (phoneNumber, recaptchaVerifier) => {
  try {
    console.log('sendPhoneOtp called with:', { phoneNumber, hasVerifier: !!recaptchaVerifier });

    // Validate phone number
    if (!phoneNumber || !phoneNumber.trim()) {
      throw new Error("Phone number is required");
    }

    if (!recaptchaVerifier) {
      throw new Error("reCAPTCHA verifier is required");
    }

    const auth = getAuth();

    if (!auth) {
      throw new Error("Firebase Auth instance not available");
    }

    console.log('Attempting to send OTP to:', phoneNumber.trim());
    console.log('reCAPTCHA verifier type:', typeof recaptchaVerifier);

    // Send OTP using Firebase Auth
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber.trim(), recaptchaVerifier);

    console.log('OTP sent successfully via Firebase');

    return {
      success: true,
      message: 'OTP sent successfully',
      confirmationResult
    };
  } catch (error) {
    console.error("Detailed error sending OTP via Firebase:", error);
    console.error("Error code:", error.code);
    console.error("Error message:", error.message);
    console.error("Error stack:", error.stack);

    // Handle specific Firebase Auth errors
    if (error.code) {
      switch (error.code) {
        case 'auth/invalid-phone-number':
          throw new Error("Invalid phone number format. Please use international format (e.g., +1234567890)");
        case 'auth/too-many-requests':
          throw new Error("Too many requests. Please try again later");
        case 'auth/user-disabled':
          throw new Error("This phone number has been disabled");
        case 'auth/quota-exceeded':
          throw new Error("SMS quota exceeded. Please try again later");
        case 'auth/internal-error':
          throw new Error("Internal authentication error. Please check Firebase configuration and ensure Phone Authentication is enabled");
        case 'auth/operation-not-allowed':
          throw new Error("Phone authentication is not enabled. Please enable it in Firebase Console");
        case 'auth/captcha-check-failed':
          throw new Error("reCAPTCHA verification failed. Please try again");
        default:
          throw new Error(error.message || "Failed to send OTP");
      }
    }

    throw new Error("Failed to send OTP via Firebase");
  }
};

const verifyPhoneOtp = async (confirmationResult, otpCode, serverlessFunctions, config) => {
  console.log('📱 Starting phone OTP verification...');
  try {
    // Validate inputs
    if (!confirmationResult) {
      throw new Error("Confirmation result is required");
    }
    if (!otpCode || !otpCode.trim()) {
      throw new Error("OTP code is required");
    }

    // Verify the OTP code
    const result = await confirmationResult.confirm(otpCode.trim());

    console.log('✅ Phone OTP verified successfully:', result.user.uid);

    // Create session cookie for SSO if enabled and phone verification was successful
    // This is wrapped in a separate try-catch to ensure SSO failures don't affect phone sign-in
    if (result && result.user && serverlessFunctions && config) {
      console.log('🍪 Attempting to create SSO session cookie for phone sign-in...');
      try {
        const sessionResult = await createAndStoreSessionCookie(
          serverlessFunctions,
          result.user,
          config
        );

        if (sessionResult.success) {
          console.log('✅ Session cookie created for phone sign-in');
        } else {
          console.warn('⚠️ Session cookie creation skipped or failed:', sessionResult.reason);
        }
      } catch (sessionError) {
        // Session cookie creation failed, but don't affect the phone sign-in process
        console.error('❌ Session cookie creation failed, but phone sign-in was successful:', sessionError);
      }
    } else {
      console.log('ℹ️ Skipping session cookie creation for phone sign-in:', {
        hasUser: !!(result && result.user),
        hasServerlessFunctions: !!serverlessFunctions,
        hasConfig: !!config
      });
    }

    return {
      success: true,
      message: 'Phone number verified successfully',
      user: result.user
    };
  } catch (error) {
    console.error("Error verifying phone OTP:", error);

    // Handle specific Firebase Auth errors
    if (error.code) {
      switch (error.code) {
        case 'auth/invalid-verification-code':
          throw new Error("Invalid verification code");
        case 'auth/code-expired':
          throw new Error("Verification code has expired");
        default:
          throw new Error(error.message || "Failed to verify OTP");
      }
    }

    throw new Error("Failed to verify phone OTP");
  }
};

const useSignOutAndReload = (properties, config) => {
  const useSignOutAndReload = async () => {
    console.log('🚪 Starting sign-out and reload...');

    // Revoke session on server FIRST
    try {
      await revokeStoredSessionCookie(properties.serverlessFunctions, config);
    } catch (revokeError) {
      console.warn('⚠️ Session revocation failed (non-critical):', revokeError);
    }

    // Clear session cookie locally and WAIT for it
    try {
      clearSessionCookie(config);
      // Give it a moment to ensure cookie is cleared
      await new Promise(resolve => setTimeout(resolve, 100));
    } catch (sessionError) {
      console.warn('⚠️ Session cookie clearing failed during sign-out:', sessionError);
    }

    // Then sign out from Firebase Auth
    try {
      await properties.auth.signOut();
      console.log('✅ Signed out from Firebase Auth');
    } catch (authError) {
      console.error('❌ Firebase sign-out failed:', authError);
    }

    // Reload after sign-out completes
    window.location.reload();
  };
  properties.signOutAndReload = useSignOutAndReload;
}

const useSignOut = (properties, config) => {
  const navigate = useNavigate();
  const signOut = async (redirectTo) => {
    console.log('🚪 Starting sign-out...');

    // Revoke session on server FIRST
    try {
      await revokeStoredSessionCookie(properties.serverlessFunctions, config);
    } catch (revokeError) {
      console.warn('⚠️ Session revocation failed (non-critical):', revokeError);
    }

    // Clear session cookie locally and WAIT for it
    try {
      clearSessionCookie(config);
      // Give it a moment to ensure cookie is cleared
      await new Promise(resolve => setTimeout(resolve, 100));
      console.log('✅ Session cookie cleared');
    } catch (sessionError) {
      console.warn('⚠️ Session cookie clearing failed during sign-out:', sessionError);
    }

    // Then sign out from Firebase Auth
    try {
      await properties.auth.signOut();
      console.log('✅ Signed out from Firebase Auth');
    } catch (authError) {
      console.error('❌ Firebase sign-out failed:', authError);
    }

    // Navigate or reload after sign-out completes
    if (redirectTo) {
      const navigateTo = "/login?redirectTo=" + redirectTo;
      navigate(navigateTo, { replace: true, preventScrollReset: false });
    } else {
      window.location.reload();
    }
  };
  properties.signOut = signOut;
};

const useFirebaseSignInWithEmailAndPassword = (properties, config) => {
  const [signInWithEmailAndPassword] = useSignInWithEmailAndPassword(
    properties.auth
  );

  const doSignInWithEmailAndPassword = async (email, password, remember) => {
    console.log('🔐 Starting email/password sign-in...');
    try {
      await setPersistence(
        properties.auth,
        remember ? indexedDBLocalPersistence : browserSessionPersistence
      );

      const val = await signInWithEmailAndPassword(email, password);
      console.log('✅ Firebase authentication successful');

      // Create session cookie for SSO if enabled and sign-in was successful
      // This is wrapped in a separate try-catch to ensure SSO failures don't affect normal sign-in
      if (val && val.user && properties.serverlessFunctions) {
        console.log('🍪 Attempting to create SSO session cookie...');
        try {
          const sessionResult = await createAndStoreSessionCookie(
            properties.serverlessFunctions,
            val.user,
            config
          );

          if (sessionResult.success) {
            console.log('✅ Session cookie created for email sign-in');
          } else {
            console.warn('⚠️ Session cookie creation skipped or failed:', sessionResult.reason);
          }
        } catch (sessionError) {
          // Session cookie creation failed, but don't affect the main sign-in process
          console.error('❌ Session cookie creation failed, but sign-in was successful:', sessionError);
        }
      } else {
        console.log('ℹ️ Skipping session cookie creation:', {
          hasUser: !!(val && val.user),
          hasServerlessFunctions: !!properties.serverlessFunctions
        });
      }

      return val !== undefined;
    } catch (error) {
      console.error('Sign in error:', error);

      // Provide user-friendly error messages
      const errorMessages = {
        'auth/user-not-found': 'No account found with this email address.',
        'auth/wrong-password': 'Incorrect password. Please try again.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/user-disabled': 'This account has been disabled.',
        'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
        'auth/network-request-failed': 'Network error. Please check your connection.'
      };

      const userFriendlyMessage = errorMessages[error.code] || 'An unexpected error occurred. Please try again.';

      // You can emit this error to a global error handler or state management
      throw new Error(userFriendlyMessage);
    }
  };
  properties.signInWithEmailAndPassword = doSignInWithEmailAndPassword;
};

const useFirebaseSignInWithCustomToken = (properties, config) => {
  const signInWithToken = async (customToken, remember = false) => {
    try {
      await setPersistence(
        properties.auth,
        remember ? indexedDBLocalPersistence : browserSessionPersistence
      );

      const result = await signInWithCustomToken(properties.auth, customToken);

      // Create session cookie for SSO if enabled and sign-in was successful
      // This is wrapped in a separate try-catch to ensure SSO failures don't affect normal sign-in
      if (result && result.user && properties.serverlessFunctions) {
        try {
          const sessionResult = await createAndStoreSessionCookie(
            properties.serverlessFunctions,
            result.user,
            config
          );

          if (sessionResult.success) {
            console.log('Session cookie created for custom token sign-in');
          } else {
            console.log('Session cookie creation skipped or failed:', sessionResult.reason);
          }
        } catch (sessionError) {
          // Session cookie creation failed, but don't affect the main sign-in process
          console.warn('Session cookie creation failed, but sign-in was successful:', sessionError);
        }
      }

      return result !== undefined;
    } catch (error) {
      console.error('Custom token sign in error:', error);

      // Provide user-friendly error messages
      const errorMessages = {
        'auth/invalid-custom-token': 'Invalid verification code. Please try again.',
        'auth/custom-token-mismatch': 'The verification code is invalid.',
        'auth/network-request-failed': 'Network error. Please check your connection.'
      };

      const userFriendlyMessage = errorMessages[error.code] || 'An unexpected error occurred during verification.';
      throw new Error(userFriendlyMessage);
    }
  };
  properties.signInWithCustomToken = signInWithToken;
};

const useSubmitForm = (properties, _) => {
  const submitFormWithPathAndValues = async (collectionPath, formValues) => {
    if (collectionPath && formValues) {
      if (collectionPath.startsWith("/")) {
        collectionPath = collectionPath.substring(1);
      }
      try {
        const docRef = await addDoc(
          collection(properties.firestore.instance, collectionPath), formValues);
        return docRef.id;
      } catch (error) {
        return null;
      }
    } else {
      throw new Error("Collection path and/or form values are required.");
    }
  };
  properties.submitFormWithPathAndValues = submitFormWithPathAndValues;
}

const useUnauthenticatedFirebaseSendPasswordResetLink = (
  properties,
  dispatch
) => {
  const [sendPasswordResetLink] = useSendPasswordResetEmail(properties.auth);

  const doSendPasswordResetLink = async (email) => {
    const result = await sendPasswordResetLink(email)
      .then((value) => value)
      .catch(() => {
        return false;
      });
    return result;
  };
  properties.unauthenticatedSendPasswordResetLink = doSendPasswordResetLink;
};

const useDeleteUser = (properties, dispatch) => {
  properties.deleteUser = async () => {
    const deleteUserFunction = httpsCallable(
      properties.serverlessFunctions.instance,
      properties.serverlessFunctions.methods.deleteUser
    );
    const result = await deleteUserFunction({
      collectionName: properties.firestore.user.collectionName,
      uid: properties.userProfile.attributes.uid,
    })
      .then((value) => {
        return value.data.statusCode === 202;
      })
      .catch(() => {
        return false;
      });
    return result;
  };
};

const useAuthenticatedFirebaseSendPasswordResetLink = (
  properties,
  dispatch
) => {
  const [sendPasswordResetLink] = useSendPasswordResetEmail(properties.auth);

  const doSendPasswordResetLink = async () => {
    const result = await sendPasswordResetLink(
      properties.auth.currentUser.email
    )
      .then((value) => value)
      .catch(() => {
        return false;
      });
    return result;
  };
  properties.sendPasswordResetLink = doSendPasswordResetLink;
};

const useSessionCookieAutoSignIn = (properties, config) => {
  const { user, loading, serverlessFunctions } = properties;

  // Only attempt auto sign-in if:
  // 1. User is not already signed in
  // 2. Auth is not loading
  // 3. SSO is enabled
  // 4. ServerlessFunctions are available
  useMemo(() => {
    if (!user && !loading && serverlessFunctions && config) {
      try {
        const ssoConfig = getOrDefault(config.application?.auth?.sso, {});

        if (!ssoConfig.domain) {
          ssoConfig.domain = getDomain(window.location.hostname);
        }

        if (ssoConfig.enabled && ssoConfig.cookieKey && ssoConfig.domain) {
          console.log('🔍 Checking for session cookie auto sign-in...');

          // Add a small delay to ensure any sign-out operations have completed
          setTimeout(() => {
            // Wrap entire auto sign-in process to ensure it's completely non-blocking
            Promise.resolve()
              .then(() => verifyStoredSessionCookie(serverlessFunctions, config))
              .then((result) => {
                if (result.success && result.customToken) {
                  console.log('✅ Valid session cookie found, signing in automatically...');
                  // Use the custom token to sign in with additional timeout protection
                  return Promise.race([
                    signInWithCustomToken(properties.auth, result.customToken),
                    new Promise((_, reject) =>
                      setTimeout(() => reject(new Error('Auto sign-in timeout')), 15000)
                    )
                  ]);
                } else {
                  console.log('ℹ️ No valid session cookie found:', result.reason);
                  return null;
                }
              })
              .then((signInResult) => {
                if (signInResult) {
                  console.log('✅ Auto sign-in via session cookie successful');
                }
              })
              .catch((error) => {
                // Auto sign-in failed, but this should never block the app
                console.warn('⚠️ Auto sign-in via session cookie failed (non-critical):', error);
              });
          }, 200); // Small delay to ensure sign-out has completed
        } else {
          console.log('ℹ️ SSO auto sign-in not configured or not enabled');
        }
      } catch (error) {
        // Catch any synchronous errors to ensure they don't propagate
        console.warn('⚠️ Error during auto sign-in setup (non-critical):', error);
      }
    }
  }, [user, loading, serverlessFunctions, properties.auth, config]);
};

const useCompleted = (properties) => {
  // dispatch({ type: "SET_COMPLETED", payload: { completed: true} })
  properties.completed = true;
};

export default useFirebase;
